import { AlertIconWithOrangeBg, NavCloseIcon } from 'assets/icons';
import { CustomButton, CustomModal } from 'components';
import { FC } from 'react';
import { VoidFn } from 'types/baseTypes';
import { InvoiceErrorFields } from '../create-invoice/types';
import { getAmountWithCurrency } from 'utils/amountUtils';
import { useNavigate } from 'react-router-dom';
import routesPath from 'routes/RoutesPath';
import { useKycLimitRevisionMessaging } from 'hooks/useKycLimitRevisionMessaging';

interface InvoiceErrorsPopupProps {
  onClose: VoidFn;
  errorFields: InvoiceErrorFields;
  minimumAmount: number;
  maximumAmount: number;
  taxEncouragedThreshold?: string;
  taxMandatoryThreshold?: string;
  onContinue: VoidFn;
}

const InvoiceErrorsPopup: FC<InvoiceErrorsPopupProps> = ({
  onClose,
  errorFields,
  minimumAmount,
  maximumAmount,
  onContinue
}) => {
  const navigate = useNavigate();
  const { content, shouldShow } = useKycLimitRevisionMessaging();
  const shouldShowKycOneLiner = shouldShow && errorFields.totalAmount && content;

  const routeToPaymentSettings = () => {
    navigate(routesPath.PAYMENT_SETTINGS);
  };

  return (
    <CustomModal open={true} width="md:w-[335px]">
      <div className="relative">
        <div className="absolute right-2.5 top-2.5 cursor-pointer" onClick={onClose}>
          <NavCloseIcon />
        </div>
        <div className="flex flex-col items-center gap-3.5 px-8 py-4">
          <AlertIconWithOrangeBg />

          <div className="text-center text-17px font-semibold text-primaryText">
            Please complete the required fields on your invoice
          </div>
          <ul className="list-disc space-y-4 pl-5 text-sbase text-primaryText">
            {errorFields.customer && <li>Add a customer</li>}
            {errorFields.totalAmount && (
              <li>
                Adjust invoice total to be between {getAmountWithCurrency(minimumAmount)} and{' '}
                {getAmountWithCurrency(maximumAmount)}
              </li>
            )}
            {shouldShowKycOneLiner && <li>{content.oneLiner}</li>}
            {errorFields.paymentMethods && (
              <li>
                Enable at least eligible 1 payment method for your team.{' '}
                <span className="cursor-pointer font-semibold text-secondary" onClick={routeToPaymentSettings}>
                  View settings&gt;
                </span>{' '}
              </li>
            )}
            {errorFields.taxRequiredAboveMandatory && (
              <li>Tax is required for invoices of this size. Please add tax details to continue.</li>
            )}
            {errorFields.taxEncouragedWarning && <li>Tax is typically included on invoices of this size.</li>}
            {errorFields.customerFirstName && (
              <li>Customer first name must be at least 3 characters and different from last name.</li>
            )}
            {errorFields.customerLastName && (
              <li>Customer last name is required and should be at least 1 character long.</li>
            )}
            {errorFields.emailOrPhone && <li>Customer email or phone is required</li>}
            {errorFields.serviceOrBillingAddress && <li>Customer service address or billing address is required</li>}
            {errorFields.serviceAddressRequired && (
              <li>Please provide a service address with street, city, state, and ZIP code.</li>
            )}
            {errorFields.phoneNumberRequired && <li>Please provide your customer&apos;s phone number.</li>}
          </ul>
        </div>
        <div className="m-4">
          <CustomButton id="invoice-error-confirm" onClick={onContinue} className="h-10 w-full">
            Got it
          </CustomButton>
        </div>
      </div>
    </CustomModal>
  );
};

export default InvoiceErrorsPopup;
